import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Rocket, CheckCircle2, ArrowRight, Utensils, Smile, Stethoscope, Wrench,
  Briefcase, Clock, TrendingUp, Users, Shield, Sparkles, MessageCircle,
} from "lucide-react";
import { goTiers, goPacks } from "@/lib/api";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";

/** WebsiteTech Growth Operator — public landing.
 *  One dominant conversion goal: "Build My Free Growth Blueprint".
 *  Structured per master spec §15 with an outcome-first hero, one-CTA funnel. */

const PACK_ICONS = {
  restaurant: Utensils, dentist: Smile, "medical-clinic": Stethoscope,
  "home-services": Wrench, "professional-services": Briefcase,
};

const OUTCOMES = [
  { icon: Clock, title: "Save 10+ hours a week", body: "AI agents handle repetitive marketing, follow-up and admin work." },
  { icon: TrendingUp, title: "Faster response, more bookings", body: "Missed calls, forms and DMs never wait — day or night." },
  { icon: Users, title: "One customer memory", body: "Every lead, call and message rolls up into one shared record." },
  { icon: Shield, title: "Clear approvals, no surprises", body: "You approve public content and spend. Full audit history included." },
];

const DIFFS = [
  { label: "Website & conversion system",   go: true,  agency: "usually", freelancer: "sometimes", tool: "partial" },
  { label: "Specialist AI agents",          go: true,  agency: "rarely",  freelancer: "sometimes", tool: true },
  { label: "Optional human review",         go: "opt", agency: "sometimes", freelancer: true,       tool: false },
  { label: "Unified customer record",       go: true,  agency: false,     freelancer: "sometimes", tool: false },
  { label: "Multilingual content",          go: true,  agency: "sometimes", freelancer: "sometimes", tool: "partial" },
  { label: "Attribution & time-saved",      go: true,  agency: "rarely",  freelancer: true,        tool: "partial" },
  { label: "Industry playbooks",            go: true,  agency: "limited", freelancer: "sometimes", tool: "generic" },
  { label: "Month-to-month & easy exit",    go: true,  agency: "rarely",  freelancer: "sometimes", tool: true },
];

const FAQ = [
  { q: "Is this replacing my website?", a: "No. It builds on WebsiteTech's conversion-focused site — or works with your existing site — and layers on the AI agents, automation and analytics that actually generate leads." },
  { q: "Do I need to know AI to use it?", a: "No. You submit requests in plain language ('promote our slow Tuesdays') and the Growth Operator picks the right agents, drafts the work, and asks for your approval before publishing." },
  { q: "Can I cancel any time?", a: "Yes. All plans are month-to-month. You can export your customer data and content at any time." },
  { q: "Where does the human help come in?", a: "AI Managed adds human review of critical outputs. Dedicated adds a named Growth Operator who owns the roadmap and reviews with you monthly." },
  { q: "What happens after I submit a Blueprint?", a: "You receive an instant readiness score, a 30/60/90-day plan and tier recommendation. If it looks right, we schedule a 20-minute walk-through." },
];

const cellFor = (v) => {
  if (v === true) return <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-500" />;
  if (v === false) return <span className="text-slate-300">—</span>;
  if (v === "opt") return <Badge className="bg-sky-100 text-sky-800 border-sky-200">Optional</Badge>;
  return <span className="text-xs text-slate-500 capitalize">{v}</span>;
};

export default function GrowthOperatorLanding() {
  const [tiers, setTiers] = useState([]);
  const [packs, setPacks] = useState([]);

  usePageSeo(PAGE_SEO["/growth-operator"]);
  useBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Growth Platform", url: "/tools" }, { name: "Growth Operator", url: "/growth-operator" }]);

  useEffect(() => {
    goTiers().then(r => setTiers(r.tiers || []));
    goPacks().then(r => setPacks(r.packs || []));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="go-landing">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-orange-50 via-white to-sky-50">
        <div className="absolute inset-0 opacity-70" style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, rgba(251,146,60,.20), transparent 40%), radial-gradient(circle at 85% 75%, rgba(56,189,248,.14), transparent 40%)",
        }} />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-orange-700 backdrop-blur">
            <Rocket className="h-3.5 w-3.5" /> WebsiteTech Growth Operator
          </div>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-black leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl">
            Stop managing marketing tools. <span className="text-orange-600">Start growing your business.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-700">
            One coordinated growth system — conversion website, specialist AI assistants,
            automated follow-up, analytics and optional human support. We build it,
            operate it and improve it, so you can run your business.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="h-12 bg-orange-500 px-6 text-base font-bold text-white shadow-md shadow-orange-500/20 hover:bg-orange-400" data-testid="go-cta-blueprint">
              <Link to="/growth-operator/blueprint">
                Build My Free Growth Blueprint <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 border-slate-300 bg-white/70 text-slate-800 hover:bg-white" data-testid="go-cta-how">
              <a href="#how-it-works">See how it works</a>
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500">
            <span>· Month-to-month · No lock-in</span>
            <span>· You approve before we publish</span>
            <span>· Export your data any time</span>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="mx-auto max-w-6xl px-4 py-20" data-testid="go-outcomes">
        <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">
          The outcome, not the toolbox.
        </h2>
        <p className="mt-3 max-w-2xl text-slate-600">
          We do not sell dashboards. We sell time back, faster responses,
          more qualified inquiries and a clear picture of what is producing revenue.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {OUTCOMES.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="border-slate-200 p-6" data-testid={`go-outcome-${title.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/-+$/,"")}`}>
              <Icon className="h-6 w-6 text-orange-500" />
              <div className="mt-3 font-heading text-lg font-bold text-slate-900">{title}</div>
              <div className="mt-1 text-sm text-slate-600">{body}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works — orchestrated team */}
      <section id="how-it-works" className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">How the Growth Operator works</h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            One primary Growth Operator orchestrates a small team of specialist AI agents.
            You approve high-stakes work. A human steps in when it matters.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { step: "1", title: "Discover", body: "We run your free Growth Blueprint — site audit, bottleneck interview, industry pack fit.", icon: Sparkles },
              { step: "2", title: "Configure", body: "Choose a service tier. We install the specialist agents, connect channels and load your knowledge base.", icon: Users },
              { step: "3", title: "Operate & improve", body: "Every month: a plan, approved work, measurable outcomes and a 1-page executive summary.", icon: TrendingUp },
            ].map(({ step, title, body, icon: Icon }) => (
              <Card key={step} className="border-slate-200 bg-white p-6" data-testid={`go-step-${step}`}>
                <div className="flex items-center gap-3">
                  <div className="grid h-8 w-8 place-items-center rounded-full bg-orange-500 font-heading text-sm font-black text-white">{step}</div>
                  <Icon className="h-5 w-5 text-orange-500" />
                </div>
                <div className="mt-3 font-heading text-lg font-bold text-slate-900">{title}</div>
                <div className="mt-1 text-sm text-slate-600">{body}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industry packs */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">Industry Growth Packs</h2>
            <p className="mt-3 max-w-2xl text-slate-600">
              Verified playbooks trained for your industry — agents, KPIs and workflows chosen for the problems your business actually has.
            </p>
          </div>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {packs.map((p) => {
            const Icon = PACK_ICONS[p.slug] || Briefcase;
            return (
              <Link key={p.slug} to={`/growth-operator/packs/${p.slug}`} data-testid={`go-pack-${p.slug}`}>
                <Card className="border-slate-200 p-6 transition hover:border-orange-300 hover:shadow-md">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-orange-500" />
                    <div className="font-heading text-base font-bold text-slate-900">{p.name}</div>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{p.problem}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.agents.slice(0, 4).map(a => (
                      <span key={a} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">{a}</span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-orange-600">
                    See pack details <ArrowRight className="h-3 w-3" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Tiers */}
      <section className="border-y border-slate-200 bg-slate-50" data-testid="go-tiers">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">Three ways to work with us</h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            Start where you are. Move up when the results speak for themselves.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {tiers.map((t, i) => (
              <Card key={t.id} className={`relative border-slate-200 bg-white p-6 ${i === 1 ? "ring-2 ring-orange-400" : ""}`} data-testid={`go-tier-${t.id}`}>
                {i === 1 && (
                  <div className="absolute -top-3 left-4 rounded-full bg-orange-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                    Most popular
                  </div>
                )}
                <div className="font-heading text-xl font-black text-slate-900">{t.name}</div>
                <div className="mt-1 text-sm text-slate-500">{t.tagline}</div>
                <div className="mt-4 font-heading text-2xl font-black text-orange-600">{t.price_display}</div>
                <div className="mt-1 text-xs text-slate-500">{t.best_for}</div>
                <ul className="mt-4 space-y-2">
                  {t.includes.map(inc => (
                    <li key={inc} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{inc}
                    </li>
                  ))}
                </ul>
                <Button asChild className="mt-6 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid={`go-tier-cta-${t.id}`}>
                  <Link to="/growth-operator/blueprint">Get a custom Blueprint</Link>
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-6xl px-4 py-20" data-testid="go-comparison">
        <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">
          Why not an agency, a freelancer or another AI tool?
        </h2>
        <p className="mt-3 max-w-2xl text-slate-600">
          They each solve part of the problem. Growth Operator combines strategy, execution, human review and measurement in one accountable system.
        </p>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left uppercase tracking-wider text-[11px] text-slate-500">
              <tr>
                <th className="px-4 py-3">Capability</th>
                <th className="px-4 py-3 text-center">Growth Operator</th>
                <th className="px-4 py-3 text-center">Marketing agency</th>
                <th className="px-4 py-3 text-center">Freelancer</th>
                <th className="px-4 py-3 text-center">Individual AI tool</th>
              </tr>
            </thead>
            <tbody>
              {DIFFS.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-slate-800">{r.label}</td>
                  <td className="px-4 py-3 text-center">{cellFor(r.go)}</td>
                  <td className="px-4 py-3 text-center">{cellFor(r.agency)}</td>
                  <td className="px-4 py-3 text-center">{cellFor(r.freelancer)}</td>
                  <td className="px-4 py-3 text-center">{cellFor(r.tool)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Trust */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">Trust, not lock-in.</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Month-to-month plans", b: "Cancel any time — export your customer data and content in one click." },
              { t: "You approve before we publish", b: "No public content, ad spend or mass messaging is sent without your approval." },
              { t: "Ethical human support", b: "Human specialists are trained, paid fairly and reviewed on outcomes — not surveillance." },
              { t: "Verifiable results", b: "Every claim ties back to a measurable KPI. Estimates are labeled as estimates." },
            ].map(({ t, b }) => (
              <Card key={t} className="border-slate-200 bg-white p-6">
                <Shield className="h-5 w-5 text-emerald-500" />
                <div className="mt-2 font-heading text-base font-bold text-slate-900">{t}</div>
                <div className="mt-1 text-xs text-slate-600">{b}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-20" data-testid="go-faq">
        <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">Frequently asked</h2>
        <div className="mt-8 space-y-4">
          {FAQ.map((f, i) => (
            <details key={i} className="rounded-xl border border-slate-200 bg-white p-4">
              <summary className="cursor-pointer font-heading text-base font-bold text-slate-900">{f.q}</summary>
              <p className="mt-2 text-sm text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-gradient-to-br from-white via-orange-50 to-amber-50">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center">
          <MessageCircle className="mx-auto h-8 w-8 text-orange-500" />
          <h2 className="mt-4 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Ready to work less and grow more?
          </h2>
          <p className="mt-3 text-slate-700">
            Get your free Growth Blueprint. Readiness score, 30/60/90-day plan, tier recommendation — in about 3 minutes.
          </p>
          <Button asChild size="lg" className="mt-8 h-12 bg-orange-500 px-8 text-base font-bold text-white shadow-md shadow-orange-500/20 hover:bg-orange-400" data-testid="go-cta-blueprint-final">
            <Link to="/growth-operator/blueprint">Build My Free Growth Blueprint <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
